import React from "react"
import {
  Modal,
  Descriptions,
  Tag,
  Space,
  Typography,
  Button,
} from "@mimber/ui-web"
import {
  MailOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
} from "@mimber/ui-web"
import { formatTableDate } from "@mimber/ui-web"
import type { InvitationDetailsModalProps } from "./InvitationDetailsModal.types"
import type { InvitationStatus } from "@mimber/schema"
import { InvitationActionsDropdown } from "@/components/dropdowns"

const { Text } = Typography

const STATUS_COLORS: Record<InvitationStatus, string> = {
  PENDING: "processing",
  ACCEPTED: "success",
  EXPIRED: "default",
  REVOKED: "error",
}

const STATUS_ICONS: Record<InvitationStatus, React.ReactNode> = {
  PENDING: <ClockCircleOutlined />,
  ACCEPTED: <MailOutlined />,
  EXPIRED: <CloseCircleOutlined />,
  REVOKED: <CloseCircleOutlined />,
}

/**
 * Modal component for viewing invitation details
 */
export const InvitationDetailsModal: React.FunctionComponent<
  InvitationDetailsModalProps
> = ({ invitation, ...modalProps }) => {
  const invitedByUser = invitation.invitedByMembership?.user

  return (
    <Modal
      title={
        <Space>
          <MailOutlined />
          <span>Invitation Details</span>
        </Space>
      }
      footer={[
        <Button key="close" onClick={modalProps.onCancel as () => void}>
          Close
        </Button>,
      ]}
      width={700}
      {...modalProps}
    >
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        {/* Actions Dropdown */}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <InvitationActionsDropdown
            invitation={invitation}
            onActionComplete={modalProps.onCancel as () => void}
            iconOnly={false}
          />
        </div>

        <Descriptions column={1} bordered>
          <Descriptions.Item label="Name">
            <Text strong>{invitation.name}</Text>
          </Descriptions.Item>

          <Descriptions.Item label="Email">
            <Text>{invitation.email}</Text>
          </Descriptions.Item>

          <Descriptions.Item label="Status">
            <Tag
              color={STATUS_COLORS[invitation.status]}
              icon={STATUS_ICONS[invitation.status]}
            >
              {invitation.status}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label="Membership Tier">
            {invitation.tier ? (
              <Tag color="blue">{invitation.tier.name}</Tag>
            ) : (
              <Text type="secondary">No tier assigned</Text>
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Invited By">
            {invitedByUser ? (
              <Text>
                {invitedByUser.firstName} {invitedByUser.lastName} (
                {invitedByUser.email})
              </Text>
            ) : (
              <Text type="secondary">—</Text>
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Sent Date">
            <Space>
              <ClockCircleOutlined />
              <Text>{formatTableDate(invitation.sentAt)}</Text>
            </Space>
          </Descriptions.Item>

          {invitation.acceptedAt && (
            <Descriptions.Item label="Accepted Date">
              <Space>
                <ClockCircleOutlined />
                <Text>{formatTableDate(invitation.acceptedAt)}</Text>
              </Space>
            </Descriptions.Item>
          )}

          <Descriptions.Item label="Expires At">
            <Space>
              <ClockCircleOutlined />
              <Text>{formatTableDate(invitation.expiresAt)}</Text>
            </Space>
          </Descriptions.Item>

          {invitation.reminderSentAt && (
            <Descriptions.Item label="Reminder Sent">
              <Space>
                <ClockCircleOutlined />
                <Text>{formatTableDate(invitation.reminderSentAt)}</Text>
              </Space>
            </Descriptions.Item>
          )}

          <Descriptions.Item label="Created">
            <Text type="secondary">
              {formatTableDate(invitation.createdAt)}
            </Text>
          </Descriptions.Item>

          <Descriptions.Item label="Last Updated">
            <Text type="secondary">
              {formatTableDate(invitation.updatedAt)}
            </Text>
          </Descriptions.Item>
        </Descriptions>
      </Space>
    </Modal>
  )
}
